<template>
  <div :class="['Countdown', locale]" ref="countdown">
    <div ref="timer">
      <h4>{{ getSeconds }}</h4>
      <img ref="countdown_bg" class="counter_bg" src="@/assets/images/countdown_bg.png" alt="countdown background">
      <img class="counter_line" src="@/assets/images/countdown_line.png" alt="glow">
    </div>
    <div class="seconds" ref="seconds">
      <LottieWrapper :options="getOptions" :size="animationSize" ref="Lottie_Seconds" :completeHandler="null" />
    </div>
  </div>
</template>

<script>

import LottieWrapper from '@/components/LottieWrapper'

import { SVGS } from '@/assets/svgs/locale_map'

import { gameTimerAudio, countdownAudio } from '@/audio'

import { TimelineLite, Expo, TweenLite } from 'gsap'

export default {
  name: 'Countdown',
  props: {
    totalSeconds: Number,
    onCountdownComplete: Function,
    onTrasitionReached: Function,
    transitionSeconds: Number,
  },
  components: {
    LottieWrapper
  },
  data() {
    return {
      seconds: this.totalSeconds,
      interval: null,
      animationSize: {
        width: 540,
        height: 30,
      },
      playCountdownAudio: true
    }
  },
  watch: {
    totalSeconds() {
      if ( !this.isVisible ) return;

      this.seconds = this.totalSeconds

      this.kickoffCountdown()
    },
    isVisible() {
      if ( this.isVisible ) {
        this.animateCountdownIn.play()
      }
    },
  },
  mounted() {
    this.initIdleStates()
    this.createAnimationTimeline()
  },
  beforeDestroy() {
    clearInterval(this.interval)
    this.animateCountdownIn.pause()
    this.animateCountdownOut.pause()
  },
  computed: {
    locale() { return this.$store.state.lang.LOCALE },
    windowState() { return this.$store.state.windowState },
    getSeconds() { return this.seconds },
    isVisible() { return this.$store.state.ui.isCountdownVisible },
    shouldPlayCountdownAudio() { return this.playCountdownAudio },
    getOptions() {
      const { Seconds } = SVGS[ this.locale ]
      return {
        animationData: Seconds.default,
        autoplay: false,
        loop: false
      }
    }
  },
  methods: {
    kickoffCountdown() {
      this.interval = setInterval( () =>  {
        this.countdown()
      }, 1000 );
    },
    initIdleStates() {

      TweenLite.to( this.$refs.countdown, 0, { opacity: 0, y: '20%' } )
    },
    createAnimationTimeline() {

      this.animateCountdownIn = new TimelineLite( { paused: true } )

      this.animateCountdownIn
        .to( this.$refs.countdown, 0.6, { opacity: 1, y: '0%', ease: Expo.easeOut }, 0.3 )
        .add( () => { this.kickoffCountdown() }, 0.6 )
        .add( () => {countdownAudio.play() }, 0.6 )
        .add( () => { this.pauseIdleAudio() }, 0.6 )

      this.animateCountdownOut = new TimelineLite( { paused: true } )

      this.animateCountdownOut
        .to( this.$refs.countdown, 0.6, { opacity: 0, ease: Expo.easeOut }, 0.6 )
        .add( () => { this.$refs.Lottie_Seconds.reverse() }, 0 )
        .add( () => { this.$store.commit( 'setCountdownVisibility', { isCountdownVisible: false } ) }, 0.9 )

      this.animateCountdownSize = new TimelineLite( { paused: true } )

      this.animateCountdownSize
        .to( this.$refs.timer, 1.5, { scale: 0.6, ease: Expo.easeInOut }, 0 )
        .to( this.$refs.countdown, 1.5, { y: `-${ window.innerHeight * 0.25 }px`, ease: Expo.easeInOut }, 0 )
        .to( this.$refs.countdown_bg, 1, { opacity: 0, ease: Expo.easeOut }, 0 )
        .add( () => { this.$refs.Lottie_Seconds.play() }, 1.4 )
        .add( () => { gameTimerAudio.play() }, 1.8 )

    },
    pauseIdleAudio() {
      this.$store.commit( 'setIsIdleLoopPlaying', { isIdleLoopPlaying: false } )
    },
    stopCountdownAudio() {
      this.playCountdownAudio = false
    },
    countdown() {
      if ( this.windowState == 'blur' ) return

      if ( this.seconds == this.transitionSeconds ) {

        this.onTrasitionReached()

        this.animateCountdownSize.play()

        this.seconds = this.seconds - 1

      } else if ( this.shouldPlayCountdownAudio && this.seconds == 1 ) {

        clearInterval( this.interval )

        this.onCountdownComplete()

      } else if ( !this.shouldPlayCountdownAudio && this.seconds < 1 ) {

        clearInterval( this.interval )

        this.onCountdownComplete()

      } else {

        this.seconds = this.seconds - 1

      }

      if ( this.shouldPlayCountdownAudio ) countdownAudio.play()
    }
  }
}
</script>

<style scoped lang="scss">

  @import '@/styles/core.scss';

  .Countdown {

    @include brightnessContrastFilter();

    position: relative;
    z-index: 2;

    &.tc,
    &.sc {
        padding-top: rem( 80 );
    }

    & > div:first-of-type {

      display: flex;
      justify-content: center;
      align-items: center;

      margin: 0 auto;

      width: rem( 80 );
      height: rem( 80 );

      position: relative;

      @include breakpoint( mobile-all ) {

        width: rem( 80 );
        height: rem( 80 );

      }

    }

    .seconds {

      .LottieWrapper {

        max-width: rem( 400 );
        height: rem( 20 );

        width: 100%;
        margin: 0 auto;

      }

    }

    .counter_bg {

      position: absolute;
      left: 0;
      right: 0;
      top: 0;

      margin: 0 auto;

      width: 100%;
      height: 100%;

    }

    .counter_line {

      position: absolute;
      top: -18%;
      left: rem( -15 );
      right: 0;

      margin: 0 auto;

      animation: internal 1.5s linear infinite;

      width: rem( 110 );
      height: rem( 110 );

      border-radius: 50%;

      @include breakpoint( mobile-all ) {

        left: rem( -15 );

        width: rem( 110 );
        height: rem( 110 );

      }

    }

    h4 {

      font-size: rem( 60 );
      font-weight: 600;

      text-shadow: 0 0 rem( 3 ) $color-white;

      @include breakpoint( mobile-all ) {

        font-size: rem( 60 );

      }

    }

    @keyframes internal {

      0% { transform: rotate(0deg) scaleX(-1)  }
      100% { transform: rotate(360deg) scaleX(-1) }

    }

  }

</style>
